import { useState } from "react";
import { Search } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, PHP } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function MyBookings() {
  const [email, setEmail] = useState("");
  const [bookings, setBookings] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async () => {
    if (!email) {
      toast.error("Enter your email to lookup bookings");
      return;
    }
    try {
      setLoading(true);
      const r = await api.get(`/bookings/by-email/${encodeURIComponent(email)}`);
      setBookings(r.data);
      if (r.data.length === 0) toast.info("No bookings found for this email.");
    } catch (e) {
      toast.error("Could not load bookings.");
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (s) => {
    const map = {
      pending: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40",
      confirmed: "bg-green-500/15 text-green-300 border-green-500/40",
      completed: "bg-blue-500/15 text-blue-300 border-blue-500/40",
      cancelled: "bg-red-500/15 text-red-300 border-red-500/40",
    };
    return map[s] || "bg-gold-500/15 text-gold-300 border-gold-500/40";
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="hero-bg pt-32 pb-20 noise" data-testid="my-bookings-page">
        <div className="max-w-4xl mx-auto px-6">
          <p className="kicker">Your Account</p>
          <h1 className="font-display text-5xl mt-3">My Bookings</h1>
          <p className="mt-4 text-foreground/65 max-w-xl">
            Look up your bookings using the email you used during reservation.
          </p>

          <div className="mt-10 glass-card rounded-2xl p-6">
            <Label className="text-xs uppercase tracking-wider">Email Address</Label>
            <div className="mt-3 flex gap-3">
              <Input
                data-testid="lookup-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="bg-ink-800/60 border-gold-500/20"
                onKeyDown={(e) => e.key === "Enter" && lookup()}
              />
              <button onClick={lookup} disabled={loading} className="btn-gold !py-2 !px-5" data-testid="lookup-bookings">
                <Search className="w-4 h-4" /> {loading ? "Searching…" : "Search"}
              </button>
            </div>
          </div>

          {bookings !== null && (
            <div className="mt-10 space-y-4">
              {bookings.length === 0 && (
                <p className="text-center text-foreground/55 py-12">No bookings found.</p>
              )}
              {bookings.map((b) => (
                <div key={b.id} className="glass-card rounded-2xl p-5 flex flex-col sm:flex-row gap-5" data-testid={`my-booking-${b.id}`}>
                  <img src={b.vehicle_image} alt={b.vehicle_name} className="w-full sm:w-40 h-32 object-cover rounded-xl" />
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-2xl text-foreground">{b.vehicle_name}</p>
                        <p className="text-xs text-gold-300 mt-1">Ref: {b.booking_ref}</p>
                      </div>
                      <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border ${statusColor(b.booking_status)}`}>
                        {b.booking_status}
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-foreground/65">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-foreground/40">Pickup</p>
                        <p className="mt-1">{new Date(b.pickup_date).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-foreground/40">Return</p>
                        <p className="mt-1">{new Date(b.return_date).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-foreground/40">Days</p>
                        <p className="mt-1">{b.days}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-foreground/40">Total</p>
                        <p className="mt-1 text-gold-300 font-medium">{PHP(b.total)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
